import React from 'react';
import { CheckboxRow } from './styles';
import { FilterField, FilterFieldContext } from './types';
import SearchField from './fields/SearchField';
import SelectField from './fields/SelectField';
import DateRangeField from './fields/DateRangeField';
import TextField from './fields/TextField';

export type FilterFieldRendererProps = {
  field: FilterField;
  ctx: FilterFieldContext;
  onSearch: () => void;
  inOverlay?: boolean;
};

const FilterFieldRenderer: React.FC<FilterFieldRendererProps> = ({
  field,
  ctx,
  onSearch,
  inOverlay = false
}) => {
  const { values, setValue, loading } = ctx;

  if (field.kind === 'search') {
    const typeName = field.typeName || 'type';
    const argumentName = field.argumentName || 'argument';
    return (
      <SearchField
        field={field}
        typeValue={values[typeName] || ''}
        argumentValue={values[argumentName] || ''}
        onTypeChange={value => setValue(typeName, value)}
        onArgumentChange={value => setValue(argumentName, value)}
        onSearch={onSearch}
        disabled={loading}
      />
    );
  }

  if (field.kind === 'select') {
    return (
      <SelectField
        field={field}
        value={values[field.name] || ''}
        onChange={value => setValue(field.name, value)}
        disabled={loading}
        inOverlay={inOverlay}
      />
    );
  }

  if (field.kind === 'dateRange') {
    return (
      <DateRangeField
        field={field}
        startValue={values[field.startName]}
        endValue={values[field.endName]}
        onChange={(start, end) => {
          setValue(field.startName, start || '');
          setValue(field.endName, end || '');
        }}
        disabled={loading}
      />
    );
  }

  if (field.kind === 'text') {
    return (
      <TextField
        field={field}
        value={values[field.name] || ''}
        onChange={value => setValue(field.name, value)}
        onSearch={onSearch}
        disabled={loading}
      />
    );
  }

  if (field.kind === 'checkbox') {
    return (
      <CheckboxRow>
        <input
          type="checkbox"
          checked={Boolean(values[field.name])}
          disabled={loading}
          onChange={e => setValue(field.name, e.target.checked)}
        />
        {field.label}
      </CheckboxRow>
    );
  }

  if (field.kind === 'custom') {
    return <>{field.render(ctx)}</>;
  }

  return null;
};

export default FilterFieldRenderer;
